// Captures of a device's web UI that the customer approved and sent us.
//
// The counterpart to `useWebCapture`, which covers taking one. This covers
// living with it afterwards: seeing what was shared, taking a copy, and
// deleting it. Those are not niceties — a consent nobody can inspect or
// withdraw the effects of is not much of a consent, and until this existed
// the only record of what a customer had handed over was in our database.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import { useActiveContextReady } from './useActiveContextReady'
import { config } from '../config'

/** Per-category counts of what the gateway removed before sending. */
export interface StoredRedactionCounts {
  headers?: number
  cookies?: number
  queryParams?: number
  bodyFields?: number
  literalMatches?: number
  total?: number
}

export interface StoredWebCapture {
  id: string
  captureId: string
  startedAt: string
  endedAt: string | null
  exchangeCount: number
  /** `guided` (the customer browsed) or `assisted_crawl`. */
  captureMode: string
  pageUrls: string[] | null
  redaction: StoredRedactionCounts | null
  createdAt: string
  /**
   * When the retention job will delete this. Server-derived from the window
   * actually enforced, so it cannot drift from what happens.
   */
  expiresAt: string
  /**
   * What the customer said they were trying to do, when they started the
   * recording themselves. Absent on prompted recordings, which answer a
   * question we posed.
   */
  note: string | null
}

export interface WebCaptureListResponse {
  captures: StoredWebCapture[]
  total: number
  retentionDays: number
}

function listKey(deviceId: string | undefined) {
  return ['deviceWebCaptures', deviceId] as const
}

export function useDeviceWebCaptures(deviceId: string | undefined) {
  const contextReady = useActiveContextReady()
  return useQuery<WebCaptureListResponse>({
    queryKey: listKey(deviceId),
    queryFn: async () => {
      if (!deviceId) throw new Error('Device ID is required')
      const response = await axios.get<WebCaptureListResponse>(
        `${config.backendApi}/api/devices/${deviceId}/web-captures`,
        { withCredentials: true },
      )
      return response.data
    },
    enabled: !!deviceId && contextReady,
  })
}

export function useDeleteWebCapture(deviceId: string | undefined) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      if (!deviceId) throw new Error('Device ID is required')
      await axios.delete(`${config.backendApi}/api/devices/${deviceId}/web-captures/${id}`, {
        withCredentials: true,
      })
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: listKey(deviceId) })
    },
  })
}

/**
 * Download a capture as a .har file.
 *
 * Fetched through axios rather than by pointing the browser at the URL: the
 * route is authenticated, and a plain link would drop the credentials the
 * rest of the app sends. The server stores gzip and returns it with
 * `Content-Encoding: gzip`, so what lands in the blob is already inflated.
 */
export async function downloadWebCapture(deviceId: string, capture: StoredWebCapture): Promise<void> {
  const response = await axios.get<Blob>(
    `${config.backendApi}/api/devices/${deviceId}/web-captures/${capture.id}/download`,
    { withCredentials: true, responseType: 'blob' },
  )
  const url = URL.createObjectURL(response.data)
  const link = document.createElement('a')
  link.href = url
  link.download = `capture-${capture.captureId}.har`
  document.body.appendChild(link)
  link.click()
  link.remove()
  // Deferred, not immediate: revoking in the same tick as the click can pull
  // the blob out from under a download the browser has not started reading
  // yet, which fails as an empty or cancelled file rather than as an error.
  setTimeout(() => { URL.revokeObjectURL(url) }, 0)
}
